import type { AdministrationMode, PkPdMethodResult } from '../types';
import { findTargetForAdministrationMode } from './doseMethodVisibility';

export interface PkpdMetricPresentation {
  metricLabel: string;
  shortLabel: string;
  predictedLabel: string;
}

export interface PkpdTargetBounds {
  min: number | null;
  max: number | null;
}

const toFiniteNumber = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

const getConcentrationMetric = (
  administrationMode: AdministrationMode,
  continuousTargetParameter: string,
): PkpdMetricPresentation => {
  if (administrationMode !== 'continuous') {
    return {
      metricLabel: '稳态谷浓度',
      shortLabel: 'Cmin,ss',
      predictedLabel: '预测稳态谷浓度',
    };
  }

  const normalizedParameter = String(continuousTargetParameter || 'Css').trim().toLowerCase();
  if (normalizedParameter.includes('cmin') || normalizedParameter.includes('trough')) {
    return {
      metricLabel: '稳态谷浓度',
      shortLabel: 'Cmin,ss',
      predictedLabel: '预测稳态谷浓度',
    };
  }
  if (normalizedParameter.includes('cmax') || normalizedParameter.includes('peak')) {
    return {
      metricLabel: '稳态峰浓度',
      shortLabel: 'Cmax,ss',
      predictedLabel: '预测稳态峰浓度',
    };
  }
  return {
    metricLabel: '稳态浓度',
    shortLabel: 'Css',
    predictedLabel: '预测稳态浓度',
  };
};

export const getPkpdMetricPresentation = (
  method: string,
  administrationMode: AdministrationMode,
  continuousTargetParameter = 'Css',
): PkpdMetricPresentation => {
  if (method === 'Target AUC') {
    return {
      metricLabel: '24小时暴露量',
      shortLabel: 'AUC₀–₂₄',
      predictedLabel: '预测24小时暴露量',
    };
  }
  if (method === 'AUC/MIC') {
    return {
      metricLabel: '24小时暴露量与MIC比值',
      shortLabel: 'AUC₀–₂₄/MIC',
      predictedLabel: '预测AUC₀–₂₄/MIC',
    };
  }
  if (method === '%fT>MIC') {
    return {
      metricLabel: '游离浓度高于MIC的时间比例',
      shortLabel: '%fT>MIC',
      predictedLabel: '预测%fT>MIC',
    };
  }
  if (method === 'Target Concentration') {
    return getConcentrationMetric(administrationMode, continuousTargetParameter);
  }
  if (method === 'Target Cmax') {
    return {
      metricLabel: '稳态峰浓度',
      shortLabel: 'Cmax,ss',
      predictedLabel: '预测稳态峰浓度',
    };
  }
  if (method === 'Cmax/MIC') {
    return {
      metricLabel: '峰浓度与MIC比值',
      shortLabel: 'Cmax/MIC',
      predictedLabel: '预测Cmax/MIC',
    };
  }
  return {
    metricLabel: method,
    shortLabel: method,
    predictedLabel: '方案预测值',
  };
};

export const resolvePkpdTargetBounds = (
  method: string,
  result: PkPdMethodResult,
  drugConfig: any,
  administrationMode: AdministrationMode,
  continuousTargetParameter = 'Css',
): PkpdTargetBounds => {
  const configuredTarget = findTargetForAdministrationMode(
    drugConfig,
    method,
    administrationMode,
    continuousTargetParameter,
  );
  return {
    min: toFiniteNumber(result.target_min) ?? toFiniteNumber(configuredTarget?.min),
    max: toFiniteNumber(result.target_max) ?? toFiniteNumber(configuredTarget?.max),
  };
};

export const resolvePkpdComparisonStatusLabel = ({
  evaluationLabel,
  predictedValue,
  targetMin,
  targetMax,
  blocksDirectionalStatus = false,
}: {
  evaluationLabel: string;
  predictedValue: unknown;
  targetMin: number | null;
  targetMax: number | null;
  blocksDirectionalStatus?: boolean;
}): string => {
  const value = toFiniteNumber(predictedValue);
  if (value == null || (targetMin == null && targetMax == null)) return '无法评价';
  if (evaluationLabel !== '未达标' || blocksDirectionalStatus) return evaluationLabel;
  if (targetMin != null && value < targetMin) return '低于目标';
  if (targetMax != null && value > targetMax) return '高于目标';
  return evaluationLabel;
};
